<script setup>
/**
 * The audit-log table, shell-free.
 *
 * Mounted by the standalone Audit Logs page and by the Activity tab on
 * Teammates. Pagination stays in the URL either way - the component pushes
 * `page` onto whatever route it is sitting on, so `?page=3` deep-links on the
 * standalone page and `?tab=activity&page=3` deep-links on the tab.
 */
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';

import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const records = useMapGetter('auditlogs/getAuditLogs');
const uiFlags = useMapGetter('auditlogs/getUIFlags');
const meta = useMapGetter('auditlogs/getMeta');
const agentList = useMapGetter('agents/getAgents');

const routerPage = computed(() => Number(route.query.page ?? 1));

const tableHeaders = computed(() => [
  t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
  t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
  t('AUDIT_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
]);

const fetchAuditLogs = page => {
  try {
    store.dispatch('auditlogs/fetch', { page });
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(auditLogItem, agentList.value);
  const translationKey = generateLogActionKey(auditLogItem);

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const onPageChange = page => {
  router.push({
    name: route.name,
    query: { ...route.query, page },
  });
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchAuditLogs(routerPage.value);
});

watch(routerPage, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    fetchAuditLogs(newPage);
  }
});
</script>

<template>
  <SettingsSection
    v-if="uiFlags.fetchingList"
    role="status"
    aria-busy="true"
    :aria-label="$t('AUDIT_LOGS.LOADING')"
  >
    <div v-for="row in 4" :key="row" class="flex items-center gap-3 px-4 py-4">
      <Skeleton variant="circle" :announce="false" class="size-8" />
      <Skeleton variant="text" :lines="2" :announce="false" class="flex-1" />
    </div>
  </SettingsSection>

  <div v-else class="flex flex-col">
    <BaseTable
      :headers="tableHeaders"
      :items="records"
      :no-data-message="$t('AUDIT_LOGS.LIST.404')"
    >
      <template #row="{ items }">
        <BaseTableRow
          v-for="auditLogItem in items"
          :key="auditLogItem.id"
          :item="auditLogItem"
        >
          <template #default>
            <BaseTableCell>
              <span class="whitespace-nowrap text-body-main text-n-slate-12">
                {{ generateLogText(auditLogItem) }}
              </span>
            </BaseTableCell>

            <BaseTableCell>
              <span class="whitespace-nowrap text-body-main text-n-slate-11">
                {{
                  messageTimestamp(
                    auditLogItem.created_at,
                    'MMM dd, yyyy hh:mm a'
                  )
                }}
              </span>
            </BaseTableCell>

            <BaseTableCell class="w-36">
              <span class="text-body-main text-n-slate-11">
                {{ auditLogItem.remote_address }}
              </span>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>
    <PaginationFooter
      v-if="records.length"
      :current-page="Number(meta.currentPage)"
      :total-items="meta.totalEntries"
      :items-per-page="meta.perPage"
      class="!px-0"
      @update:current-page="onPageChange"
    />
  </div>
</template>
